import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  DestroyRef,
  signal,
} from '@angular/core';
import { FieldType } from '../../ag1-table.component';
import { Ag1TableService } from '../../ag1-table.service';
import { map, switchMap, take } from 'rxjs/operators';
import { Ag1ListSearchComponent } from '@app/core/components/ag1-list-search/ag1-list-search.component';
import { Popover } from 'primeng/popover';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1ListSearchComponent as Ag1ListSearchComponent_1 } from '../../../ag1-list-search/ag1-list-search.component';
import { FormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '../../../../pipes/sentence.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-table-column-selection',
  templateUrl: './ag1-table-column-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Bind,
    Button,
    AngularSvgIconModule,
    Popover,
    Ag1ListSearchComponent_1,
    FormsModule,
    Tooltip,
    HumanizePipe,
    SentenceCasePipe,
  ],
})
export class Ag1TableColumnSelectionComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  fields: FieldType[];
  filteredFields: FieldType[];

  isLoading: boolean;
  isActive: boolean = false;

  @ViewChild('columnPopover') columnPopover: Popover;
  @ViewChild(Ag1ListSearchComponent) searchComponent: Ag1ListSearchComponent;

  /** (Required) Service that contains the state of the Ag1TableBase components */
  @Input() tableService: Ag1TableService;

  /** (Optional) Fields that should not be selectable */
  @Input() nonSelectableFields: string[] = [];

  // column selection
  /** (required) Event triggered when updating column. The purpose of this is to allow a feature to reset the fields to the default */
  @Output() columnSelectionResetEvent = new EventEmitter<void>();

  /** Emitted when Apply Selection is clicked, with the applied fields. Lets parents (e.g. P&L) add dimensions to groupBy when a dimension column is selected. */
  @Output() columnSelectionApplyEvent = new EventEmitter<FieldType[]>();

  // Add new property to store all fields including hidden ones
  private allFields: FieldType[];

  //signals
  readonly tooltipText = signal<string>('This is a required field.');

  constructor() {}

  ngOnInit(): void {
    if (!this.tableService) {
      console.error('Table Service is required');
      return;
    }

    this.tableService.isLoading$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((isLoading) => {
          this.isLoading = isLoading;

          // start listening to fields$ observable when isLoading$ emits a value
          return this.tableService.fields$.pipe(
            takeUntilDestroyed(this._destroyRef),
            map((fields) => {
              // Store complete fields array
              this.allFields = fields;
              // Filter out fields that shouldn't appear in UI
              this.fields = fields.filter(
                (f) => !this.nonSelectableFields.includes(f.name),
              );
              this.filteredFields = this.fields;
              this.refreshInterface();
            }),
          );
        }),
      )
      .subscribe();
  }

  /**
   * @param field - The field to check
   * @returns true unless `checkable` is explicitly `false` (undefined/omitted defaults to checkable)
   */
  isFieldCheckable(field: FieldType): boolean {
    return field?.checkable !== false;
  }

  refreshInterface(): void {
    this._cdr.detectChanges();
  }

  updateColumnOrder(field: FieldType, newIndexInput: number | string): void {
    const parsed =
      typeof newIndexInput === 'string'
        ? parseInt(newIndexInput, 10) || 0
        : newIndexInput;
    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const currentIndex = fields.findIndex(
            (f: FieldType) => f.name === field.name,
          );
          if (currentIndex === -1) return fields;
          const newIndex = Math.max(0, Math.min(parsed, fields.length - 1));
          const updatedFields: FieldType[] = [...fields];
          updatedFields.splice(currentIndex, 1);
          updatedFields.splice(newIndex, 0, field);
          updatedFields.forEach((f, index) => (f.order = index));
          return updatedFields;
        }),
      )
      .subscribe((updatedFields) => {
        this.fields = updatedFields.filter(
          (f) => !this.nonSelectableFields.includes(f.name),
        );
        this.refreshInterface();
      });
  }

  updateColumnVisibility(field: FieldType): void {
    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) => {
            if (f.name === field.name) {
              f.checked = field.checked;
            }

            // prevent sorting on hidden fields
            if (!f.checked) {
              f.sortDirection = null;
            }

            return f;
          });
          // console.log('visible fields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe(
        (fields) =>
          (this.fields = fields.filter(
            (f) => !this.nonSelectableFields.includes(f.name),
          )),
      );
  }

  applyColumnSelection(): void {
    this.closeColumnsPanel();
    // Merge UI fields with hidden fields before updating
    const updatedFields = [...this.fields];
    const hiddenFields = this.allFields.filter((f) =>
      this.nonSelectableFields.includes(f.name),
    );
    const merged = [...updatedFields, ...hiddenFields];
    this.tableService.updateFields(merged);
    this.columnSelectionApplyEvent.emit(merged);
  }

  resetColumnSelection(): void {
    this.closeColumnsPanel();
    this.columnSelectionResetEvent.emit();
  }

  closeColumnsPanel(): void {
    this.columnPopover?.hide();
  }

  resetFilteredFields(): void {
    this.filteredFields = [...this.fields];
  }

  onMenuOpen(): void {
    this.isActive = true;
    setTimeout(() => {
      this.searchComponent?.focusSearch();
    });
  }

  onMenuClose(): void {
    this.isActive = false;
    this.resetFilteredFields();
  }
}
